import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { FooterComponent } from '../footer/footer.component';
import { AuthHeaderComponent } from '../auth-header/auth-header.component';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FooterComponent, AuthHeaderComponent],
  template: `
    <main>
      <app-auth-header [showBackButton]="true"></app-auth-header>

      <div class="change-password-scroll">
      <div class="change-password-container">
      <h1 class="form-title">Passwort ändern</h1>
      
      @switch (currentStep()) {
        @case ('change-password') {
          <p class="info-text">
            Geben Sie Ihr neues Passwort ein.
          </p>

          <form [formGroup]="changePasswordForm" (ngSubmit)="onChangePassword()">
            <div class="form-group">
              <label for="newPassword">Neues Passwort:</label>
              <div class="password-wrapper">
                <input
                  id="newPassword"
                  [type]="showNewPassword() ? 'text' : 'password'"
                  formControlName="newPassword"
                  placeholder="Passwort (mind. 6 Zeichen)"
                />
                <button
                  type="button"
                  class="toggle-password-btn"
                  (click)="showNewPassword.set(!showNewPassword())"
                  tabindex="-1"
                  [attr.aria-label]="showNewPassword() ? 'Passwort verbergen' : 'Passwort anzeigen'"
                >
                  {{ showNewPassword() ? '👁️‍🗨️' : '👁️' }}
                </button>
              </div>
              @if (changePasswordForm.get('newPassword')?.hasError('required') && changePasswordForm.get('newPassword')?.touched) {
                <span class="error">Passwort ist erforderlich</span>
              }
              @if (changePasswordForm.get('newPassword')?.hasError('minlength') && changePasswordForm.get('newPassword')?.touched) {
                <span class="error">Passwort muss mind. 6 Zeichen lang sein</span>
              }
            </div>

            <div class="form-group">
              <label for="confirmPassword">Passwort wiederholen:</label>
              <div class="password-wrapper">
                <input
                  id="confirmPassword"
                  [type]="showConfirmPassword() ? 'text' : 'password'"
                  formControlName="confirmPassword"
                  placeholder="Passwort wiederholen"
                />
                <button
                  type="button"
                  class="toggle-password-btn"
                  (click)="showConfirmPassword.set(!showConfirmPassword())"
                  tabindex="-1"
                  [attr.aria-label]="showConfirmPassword() ? 'Passwort verbergen' : 'Passwort anzeigen'"
                >
                  {{ showConfirmPassword() ? '👁️‍🗨️' : '👁️' }}
                </button>
              </div>
              @if (changePasswordForm.get('confirmPassword')?.hasError('required') && changePasswordForm.get('confirmPassword')?.touched) {
                <span class="error">Passwort wiederholen ist erforderlich</span>
              }
              @if (changePasswordForm.get('confirmPassword')?.hasError('minlength') && changePasswordForm.get('confirmPassword')?.touched) {
                <span class="error">Passwort muss mind. 6 Zeichen lang sein</span>
              }
              @if (!passwordsMatch() && (changePasswordForm.get('newPassword')?.touched || changePasswordForm.get('confirmPassword')?.touched)) {
                <span class="error">Passwörter stimmen nicht überein</span>
              }
            </div>

            @if (authService.errorMessage()) {
              <div class="error-message">
                {{ authService.errorMessage() }}
              </div>
            }

            <button type="submit" [disabled]="!changePasswordForm.valid || isLoading() || !passwordsMatch()">
              @if (isLoading()) {
                Wird gesendet...
              } @else {
                Bestätigungscode anfordern
              }
            </button>
          </form>
        }

        @case ('verify-code') {
          <p class="info-text">
            Wir haben einen Bestätigungscode an <strong>{{ userEmail() }}</strong> gesendet.
            Bitte geben Sie den Code ein, um das Passwort zu ändern.
          </p>

          @if (timerSeconds() <= 0) {
            <div class="expired-message">Code ist abgelaufen. Bitte fordern Sie einen neuen Code an.</div>
          }

          <form [formGroup]="verifyCodeForm" (ngSubmit)="onVerifyCode()" [hidden]="timerSeconds() <= 0">
            <div class="form-group">
              <label for="code">Verifizierungscode:</label>
              <input
                id="code"
                type="text"
                formControlName="code"
                placeholder="6-stelliger Code"
                maxlength="6"
                (input)="onCodeInput($event)"
              />
              @if (verifyCodeForm.get('code')?.hasError('required') && verifyCodeForm.get('code')?.touched) {
                <span class="error">Code ist erforderlich</span>
              }
              @if (verifyCodeForm.get('code')?.hasError('minlength') && verifyCodeForm.get('code')?.touched) {
                <span class="error">Code muss 6 Ziffern enthalten</span>
              }
            </div>

            @if (authService.errorMessage()) {
              <div class="error-message">
                {{ authService.errorMessage() }}
              </div>
            }

            <button 
              type="button"
              (click)="onVerifyClick()"
              [disabled]="!verifyCodeForm.valid || isLoading()"
            >
              @if (isLoading()) {
                Wird verifiziert...
              } @else {
                Verifizieren
              }
            </button>
          </form>

          <div class="resend-section" [hidden]="timerSeconds() <= 0">
            <p>Code nicht erhalten?</p>
            <button 
              type="button" 
              (click)="onResendCode()"
              [disabled]="isResendLoading() || resendCooldown() > 0"
              class="resend-button"
            >
              @if (isResendLoading()) {
                Wird versendet...
              } @else if (resendCooldown() > 0) {
                Erneut versenden in {{ resendCooldown() }}s
              } @else {
                Code erneut versenden
              }
            </button>
          </div>

          @if (timerSeconds() <= 0) {
            <button type="button" (click)="onRequestNewCode()" class="retry-button">
              Neuen Code anfordern
            </button>
          }
        }

        @case ('success') {
          <div class="success-message" style="text-align: center; padding: 2rem 0;">
            ✅ Passwort erfolgreich geändert!
          </div>
          <button type="button" (click)="onSuccess()">
            Kalkulation durchführen
          </button>
        }
      }
      </div>
      </div>
    </main>

    <app-footer></app-footer>
  `,
  styles: [`
    :host {
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        Helvetica, Arial, sans-serif;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: hidden;
    }

    ::ng-deep .header-content {
      margin-left: -58px !important;
    }

    ::ng-deep .back-link {
      margin-top: 17px !important;
    }

    ::ng-deep .back-link a {
      position: relative !important;
      left: -27px !important;
    }

    main {
      flex: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0;
      min-height: 0;
      box-sizing: border-box;
    }

    .change-password-scroll {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      padding: 22px 2rem 1.5rem;
      box-sizing: border-box;
    }

    main > div:not(.header-container) {
      padding: 0 2rem;
    }

    main > div:first-child {
      padding: 0;
    }

    .change-password-container {
      max-width: 480px !important;
      margin: auto;
      padding: 40px;
      padding-bottom: 60px;
      min-height: 450px;
      border: 1px solid #ccc;
      border-radius: 8px;
      background: #f9f9f9;
      width: 100%;
      box-sizing: border-box;
    }

    @media (max-height: 750px) {
      .change-password-container {
        margin: auto;
        padding: 30px;
      }
    }

    h1 {
      text-align: center;
      padding-top: 5px;
      margin-bottom: 22px;
      color: #333;
    }

    .info-text {
      text-align: center;
      color: #666;
      margin-bottom: 20px;
      font-size: 14px;
    }

    .info-text strong {
      color: #333;
    }

    .form-group {
      margin-bottom: 17px;
      display: flex;
      flex-direction: column;
    }

    label {
      margin-bottom: 5px;
      color: #555;
      font-weight: 500;
    }

    input {
      padding: 10px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
      font-family: inherit;
    }

    input:focus {
      outline: none;
      border-color: #4CAF50;
      box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
    }

    /* Code-Eingabefeld - identisch mit Verify-Email */
    input[type="text"][maxlength="6"] {
      padding: 10px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 16px;
      font-family: monospace;
      text-align: center;
      letter-spacing: 4px;
    }

    .password-wrapper {
      position: relative;
      display: flex;
      align-items: center;
    }

    .password-wrapper input {
      width: 100%;
      padding-right: 35px;
    }

    /* Browser-native Password Reveal Button verstecken */
    .password-wrapper input::-webkit-credentials-auto-fill-button,
    .password-wrapper input::-webkit-outer-spin-button,
    .password-wrapper input::-webkit-inner-spin-button {
      display: none;
    }

    .password-wrapper input::-ms-reveal {
      display: none;
    }

    .toggle-password-btn {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      cursor: pointer;
      font-size: 18px;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #666;
      margin-top: -2px;
    }

    .toggle-password-btn:hover {
      background-color: white;
      border-radius: 4px;
    }

    .toggle-password-btn:focus {
      outline: none;
    }

    .error {
      color: #d32f2f;
      font-size: 12px;
      margin-top: 5px;
    }

    .error-message {
      background-color: #ffebee;
      color: #d32f2f;
      padding: 10px;
      border-radius: 4px;
      margin-bottom: 15px;
      border-left: 4px solid #d32f2f;
    }

    .success-message {
      background-color: #e8f5e9;
      color: #2e7d32;
      padding: 10px;
      border-radius: 4px;
      margin-bottom: 15px;
      border-left: 4px solid #2e7d32;
    }

    button {
      width: 100%;
      padding: 10px;
      background-color: #4CAF50;
      color: white;
      border: none;
      border-radius: 4px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.3s;
      margin-top: calc(1rem - 16px);
    }

    button:not(.toggle-password-btn):hover:not(:disabled) {
      background-color: #45a049;
    }

    button:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }

    .resend-section {
      margin-top: 15px;
      padding-top: 15px;
      border-top: 1px solid #ddd;
      text-align: center;
    }

    .resend-section p {
      color: #666;
      margin-bottom: 10px;
      margin-top: -5px;
      font-size: 14px;
    }

    .resend-button {
      background-color: #2196F3;
      font-size: 14px;
      margin-top: 0;
    }

    .resend-button:hover:not(:disabled) {
      background-color: #0b7dda;
    }

    .retry-button {
      background-color: #FF9800;
    }

    .retry-button:hover:not(:disabled) {
      background-color: #F57C00;
    }

    .timer-container {
      margin-bottom: 20px;
      text-align: center;
    }

    .timer {
      padding: 15px;
      background-color: #e3f2fd;
      border: 1px solid #90caf9;
      border-radius: 4px;
      color: #1976d2;
      font-size: 16px;
      font-weight: 500;
    }

    .timer.warning {
      background-color: #fff3e0;
      border-color: #ffb74d;
      color: #f57c00;
    }

    .expired-message {
      padding: 15px;
      background-color: #ffebee;
      border: 1px solid #ef5350;
      border-radius: 4px;
      color: #d32f2f;
      font-size: 14px;
    }

    .login-link {
      text-align: center;
      margin-top: 20px;
      margin-bottom: 0;
      font-size: 14px;
    }

    .login-link a {
      color: #4CAF50;
      text-decoration: none;
      font-weight: 500;
    }

    .login-link a:hover {
      text-decoration: underline;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangePasswordComponent {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  protected authService = inject(AuthService);

  currentStep = signal<'change-password' | 'verify-code' | 'success'>('change-password');
  isLoading = signal(false);
  isResendLoading = signal(false);
  resendCooldown = signal(0);
  showNewPassword = signal(false);
  showConfirmPassword = signal(false);
  userEmail = signal('');
  timerSeconds = signal(0);
  passwordsMatch = signal(true);

  changePasswordForm = this.fb.group({
    newPassword: ['', [Validators.required, Validators.minLength(6)]],
    confirmPassword: ['', [Validators.required, Validators.minLength(6)]]
  });

  verifyCodeForm = this.fb.group({
    code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
  });

  constructor() {
    // Falls Benutzer nicht authentifiziert ist, zur Login-Seite weiterleiten
    if (!this.authService.isLoggedIn()) {
      this.router.navigate(['/login']);
    }

    // E-Mail-Adresse direkt aus JWT Token dekodieren (zuverlässiger als das user Signal)
    const emailFromToken = this.authService.getEmailFromToken();
    if (emailFromToken) {
      this.userEmail.set(emailFromToken);
    }

    // Zusätzlich: Auch das user Signal überwachen falls es sich ändert
    effect(() => {
      const user = this.authService.user();
      if (user?.email) {
        this.userEmail.set(user.email);
      }
    });

    // Überwache Änderungen in beiden Passwort-Feldern
    this.changePasswordForm.get('newPassword')?.valueChanges.subscribe(() => this.checkPasswords());
    this.changePasswordForm.get('confirmPassword')?.valueChanges.subscribe(() => this.checkPasswords());
  }

  private checkPasswords() {
    const pwd = this.changePasswordForm.get('newPassword')?.value || '';
    const pwdConfirm = this.changePasswordForm.get('confirmPassword')?.value || '';
    this.passwordsMatch.set(pwd === pwdConfirm);
  }

  onChangePassword() {
    if (!this.changePasswordForm.valid || !this.passwordsMatch()) return;

    const newPassword = this.changePasswordForm.get('newPassword')?.value;
    if (!newPassword) return;

    this.isLoading.set(true);
    this.authService.errorMessage.set(null);

    this.authService.requestPasswordChangeCode(this.userEmail()).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.currentStep.set('verify-code');
        this.startTimer();
        this.startResendCooldown();
      },
      error: (err) => {
        this.isLoading.set(false);
      }
    });
  }

  onVerifyClick() {
    this.onVerifyCode();
  }

  onVerifyCode() {
    if (!this.verifyCodeForm.valid) return;

    const code = this.verifyCodeForm.get('code')?.value;
    if (!code) return;

    this.isLoading.set(true);
    this.authService.errorMessage.set(null);

    this.authService.verifyPasswordChangeCode(this.userEmail(), code).subscribe({
      next: () => {
        // Code verifiziert, jetzt Passwort ändern
        const newPassword = this.changePasswordForm.get('newPassword')?.value;
        if (newPassword) {
          this.authService.confirmPasswordChange(this.userEmail(), newPassword).subscribe({
            next: () => {
              this.isLoading.set(false);
              this.timerSeconds.set(0);
              this.currentStep.set('success');
            },
            error: (err) => {
              this.isLoading.set(false);
            }
          });
        }
      },
      error: (err) => {
        this.isLoading.set(false);
      }
    });
  }

  onCodeInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleanValue = input.value.replace(/\s/g, '');
    if (cleanValue !== input.value) {
      input.value = cleanValue;
      this.verifyCodeForm.get('code')?.setValue(cleanValue, { emitEvent: false });
    }
  }

  onResendCode() {
    if (this.userEmail()) {
      this.isResendLoading.set(true);
      this.authService.requestPasswordChangeCode(this.userEmail()).subscribe({
        next: () => {
          this.isResendLoading.set(false);
          this.startResendCooldown();
        },
        error: (err) => {
          this.isResendLoading.set(false);
        }
      });
    }
  }

  onRequestNewCode() {
    this.currentStep.set('change-password');
    this.changePasswordForm.reset();
    this.verifyCodeForm.reset();
    this.authService.errorMessage.set(null);
  }

  onSuccess() {
    this.router.navigate(['/calculator']);
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private startTimer() {
    this.timerSeconds.set(600); // 10 Minuten
    const interval = setInterval(() => {
      const current = this.timerSeconds();
      if (current > 0) {
        this.timerSeconds.set(current - 1);
      } else {
        clearInterval(interval);
      }
    }, 1000);
  }

  private startResendCooldown() {
    this.resendCooldown.set(60);
    const interval = setInterval(() => {
      const current = this.resendCooldown();
      if (current > 0) {
        this.resendCooldown.set(current - 1);
      } else {
        clearInterval(interval);
      }
    }, 1000);
  }
}
